import React from 'react';
import { Typography } from '../../../../../components';
import { useTranslations } from '../../../../../contexts/translation.context';
import { RevshareOrder } from '../../../../../api/client/clientRevshareService';

interface CampaignStatsTooltipProps {
  campaign: RevshareOrder;
}

const formatPercent = (value: number, total: number): string => {
  if (!total) return '0%';
  const percent = (value / total) * 100;
  return `${percent.toFixed(2)}%`;
};

const CampaignStatsTooltip: React.FC<CampaignStatsTooltipProps> = ({ campaign }) => {
  const { t } = useTranslations();

  const subscribedCount = campaign.subscribed_count || 0;
  const verifiedUsers = campaign.verified_users_count || subscribedCount;
  const transactionsStatistic = campaign.transactions_statistic || {
    '1': 0,
    '2': 0,
    '5': 0,
    '500': 0,
    total: '0',
    count: 0,
  };

  const purchasesCount = transactionsStatistic.count || 0;
  const purchases500 = transactionsStatistic['500'] || 0;

  const repeatedSubscriptions = campaign.repeated_subscriptions_count || 0;
  const messagesCount =
    campaign.subs?.with_gte_3_chat_messages_count || campaign.messages_3_plus_count || 0;

  const totalFans = verifiedUsers || subscribedCount;

  const stats = [
    {
      label: t('campaigns.stats.subscriptions') || 'Подписки',
      value: verifiedUsers,
      percentage: formatPercent(verifiedUsers, totalFans),
    },
    {
      label: t('campaigns.stats.repeatedSubscriptions') || 'Повторные подписки',
      value: repeatedSubscriptions,
      percentage: formatPercent(repeatedSubscriptions, totalFans),
    },
    {
      label: t('campaigns.stats.purchases') || 'Покупки',
      value: purchasesCount,
      percentage: formatPercent(purchasesCount, totalFans),
    },
    {
      label: t('campaigns.stats.purchases500') || 'Покупки на $500',
      value: purchases500,
      percentage: formatPercent(purchases500, totalFans),
    },
    {
      label: t('campaigns.stats.messages3') || '3 сообщения',
      value: messagesCount,
      percentage: formatPercent(messagesCount, totalFans),
    },
  ];

  return (
    <div className="">
      <div className="mb-2">
        <Typography variant="body2" className="text-auth-ink">
          {t('campaigns.stats.title') || 'Статистика кампании'}
        </Typography>
      </div>

      <div className="flex items-center mb-2">
        <div className="w-[10rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('campaigns.stats.parameter') || 'Параметр'}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('campaigns.stats.fans') || 'Фанатов'}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            %
          </Typography>
        </div>
      </div>

      <div>
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center py-1.5">
            <div className="w-[10rem]">
              <Typography variant="body3" className="text-auth-muted">
                {stat.label}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.value}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.percentage}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignStatsTooltip;
